import { useEffect, useMemo, useState } from "react";
import { animate } from "framer-motion";
import { Calculator, PiggyBank } from "lucide-react";
import { Reveal, ChapterTag } from "./Reveal";
import { API, fmt } from "../lib/store";
import { EASE } from "./Hero";

function AnimatedMoney({ value, className }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        const controls = animate(display, value, {
            duration: 0.7,
            ease: EASE,
            onUpdate: (v) => setDisplay(v),
        });
        return () => controls.stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return <span className={className}>{fmt(display)}</span>;
}

export default function SavingsCalculator() {
    const [products, setProducts] = useState([]);
    const [picks, setPicks] = useState({});

    useEffect(() => {
        fetch(`${API}/products`)
            .then((r) => r.json())
            .then((list) => {
                const priced = list.filter((p) => p.our_price);
                setProducts(priced);
                const defaults = {};
                priced.forEach((p) => {
                    if (!(p.category in defaults)) defaults[p.category] = p.id;
                });
                setPicks(defaults);
            })
            .catch(() => {});
    }, []);

    const byCat = useMemo(() => {
        const m = {};
        products.forEach((p) => {
            (m[p.category] = m[p.category] || []).push(p);
        });
        return m;
    }, [products]);

    const totals = useMemo(() => {
        let hd = 0,
            ours = 0;
        Object.values(picks).forEach((id) => {
            const p = products.find((x) => x.id === id);
            if (p) {
                hd += p.hd_price;
                ours += p.our_price;
            }
        });
        return { hd, ours, saved: hd - ours };
    }, [picks, products]);

    const categories = Object.keys(byCat);

    return (
        <section id="savings" className="relative py-24 md:py-36">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <ChapterTag num="04" title="Do The Math" testid="savings-chapter-tag" />
                <Reveal delay={0.05}>
                    <h2 className="mt-8 font-display font-extrabold tracking-tight text-3xl sm:text-5xl leading-[1.02]">
                        Build your kitchen. <span className="text-gold">Watch the retail total melt.</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.15}>
                    <div
                        data-testid="savings-calculator-container"
                        className="mt-12 grid lg:grid-cols-12 gap-8 rounded-2xl border border-white/10 bg-ink2/60 p-6 md:p-10 backdrop-blur-xl"
                    >
                        <div className="lg:col-span-7 space-y-4">
                            {categories.map((c) => (
                                <div key={c} className="flex flex-wrap sm:flex-nowrap items-center gap-4 border-b border-white/5 pb-4">
                                    <span className="w-44 shrink-0 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                        {c}
                                    </span>
                                    <select
                                        data-testid={`calc-select-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                        value={picks[c] || ""}
                                        onChange={(e) => setPicks((prev) => ({ ...prev, [c]: e.target.value }))}
                                        className="styled-select flex-1 cursor-pointer rounded-lg border border-white/15 bg-ink px-4 py-3 text-sm text-white focus:border-gold focus:outline-none transition-colors"
                                        aria-label={`Choose a ${c}`}
                                    >
                                        {byCat[c].map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.brand} · {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 pt-2">
                                * Live inventory from our dealer system — every price about 40%+ under retail
                            </p>
                        </div>

                        <div className="lg:col-span-5 flex flex-col justify-center gap-6 rounded-xl border border-white/10 bg-ink p-6 md:p-8">
                            <div className="flex items-center justify-between">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-hdred">
                                    At retail
                                </p>
                                <p className="font-mono text-xl text-hdred line-through decoration-2">
                                    <AnimatedMoney value={totals.hd} className="inline-block" />
                                </p>
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                                    At Karagna.com
                                </p>
                                <p className="font-display text-3xl md:text-4xl font-extrabold text-gold">
                                    <AnimatedMoney value={totals.ours} className="inline-block" />
                                </p>
                            </div>
                            <div className="flex items-center justify-between rounded-xl bg-save/10 border border-save/30 px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <PiggyBank className="h-5 w-5 text-save" />
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-save">
                                        You keep
                                    </p>
                                </div>
                                <p
                                    data-testid="savings-calculator-total-savings"
                                    className="font-display text-3xl md:text-4xl font-extrabold text-save"
                                >
                                    <AnimatedMoney value={totals.saved} className="inline-block" />
                                </p>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500">
                                <Calculator className="h-3.5 w-3.5" />
                                <p className="text-xs">
                                    Mix and match — the totals update as you pick. Whole-kitchen savings included.
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
